import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Employee,
  formatEmployeeLocationType,
  formatGender,
  getEmployeeClientCompany,
} from "../types/employees";

type EmployeeSummaryProps = {
  employee: Employee;
  showFullName?: boolean;
};

/**
 * Read-only grid of an employee's key details and note. Shared by the
 * employee page, the employee details modal and the episode intake page.
 */
function EmployeeSummary({
  employee,
  showFullName = false,
}: EmployeeSummaryProps) {
  const { t } = useTranslation();

  const dash = "-";
  const rows: { label: string; value: ReactNode }[] = [
    ...(showFullName
      ? [{ label: t("Full name"), value: employee.fullName }]
      : []),
    { label: t("National ID"), value: employee.nationalId || dash },
    {
      label: t("Client company"),
      value: getEmployeeClientCompany(employee)?.name ?? dash,
    },
    { label: t("Workstation"), value: employee.workstation?.name ?? dash },
    {
      label: t("Location"),
      value: formatEmployeeLocationType(employee.locationType),
    },
    {
      label: t("Gender"),
      value: employee.gender ? formatGender(employee.gender) : dash,
    },
    { label: t("Nationality"), value: employee.nationality || dash },
    { label: t("Department"), value: employee.department || dash },
    { label: t("Job title"), value: employee.jobTitle || dash },
    { label: t("Email"), value: employee.email || dash },
    { label: t("Phone"), value: employee.phone || dash },
    { label: t("Landline"), value: employee.landline || dash },
  ];

  return (
    <>
      <dl className="summaryGrid">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="summaryLabel">{row.label}</dt>
            <dd className="summaryValue">{row.value}</dd>
          </div>
        ))}
      </dl>

      {employee.note ? (
        <dl className="employeeDetailsNote">
          <dt className="summaryLabel">{t("Note")}</dt>
          <dd className="summaryValue">{employee.note}</dd>
        </dl>
      ) : null}
    </>
  );
}

export default EmployeeSummary;
